/* eslint-disable react-hooks/rules-of-hooks */
import { FormError } from '@kcftech/design-system';
import {
    DashboardUpdateRequest,
    ICorporationResponse,
    IDashboardResponse,
    ILocationResponse,
} from '@kcftech/prometheus-client';
import { Label } from 'components/forms/Label';
import { Textarea } from 'components/forms/Textarea';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { ContextualModalProps } from 'components/modal/ContextualModal';
import { ModalHistoryInfoRow } from 'components/modal/ModalHistoryInfoRow';
import { StatelessContextualModal } from 'components/modal/StatelessContextualModal';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useUser } from 'contexts/UserContext';
import { useIsMounted } from 'custom_hooks/useIsMounted';
import { useEffect } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import { handleSubmitErrors } from 'utils/form';

const descriptionRules: RegisterOptions = {
    maxLength: {
        value: 200,
        message: 'Description is too long.',
    },
};

const titleRules: RegisterOptions = {
    maxLength: {
        value: 128,
        message: 'Title is too long.',
    },
};

type PropsFromContextualModal = 'onSave';
export type DashboardSettingsProps = Pick<
    ContextualModalProps,
    PropsFromContextualModal
> & {
    /**
     * The dashboard data that will be viewed by the user.
     */
    dashboard: IDashboardResponse | undefined;
    /**
     * The current corporation.
     */
    corporation: ICorporationResponse;
    /**
     *
     */
    location?: ILocationResponse;
    onCancel: () => void;
};

type SettingsForm = {
    title: string;
    description: string;
    timeRange: string;
};

export function DashboardSettingsModal({
    dashboard,
    onCancel,
    onSave,
}: DashboardSettingsProps) {
    if (!dashboard) {
        return <></>;
    }

    const user = useUser();
    const { canEditPowerBiReportDashboards } = usePermissions();
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        reset,
    } = useForm<SettingsForm>({
        // Revalidate the form inputs with every focus/blur to every input.
        // WARNING: This causes many more rerenders than the default behavior.
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            title: dashboard.Name,
            description: dashboard.Description,
        },
    });

    const onSubmitSettings = async (data: SettingsForm) => {
        // Form is client-side validated at this point
        if (dashboard.Id) {
            const update = new DashboardUpdateRequest({
                Name: data.title ? data.title : dashboard.Name,
                Description: data.description,
            });
            await sdClient.updateDashboard({ id: dashboard.Id, body: update });
            if (onSave) {
                await onSave();
            }
            await user.updateFavorites();
            return true;
        }
        return false;
    };

    const isMountedRef = useIsMounted();

    // TODO: This should be updated to useQuery or a data hook
    // Update form values when dashboard changes
    useEffect(() => {
        reset({
            title: dashboard.Name,
            description: dashboard.Description,
        });
    }, [dashboard, isMountedRef, reset]);

    return (
        <StatelessContextualModal
            id="dashboard-settings"
            title="Dashboard Settings"
            overlay
            onClose={() => {
                onCancel();
            }}
            onSave={handleSubmitErrors(handleSubmit, onSubmitSettings)}
            canSave={(!isSubmitted || isValid) && !(isValidating || isSubmitting)}
            content={() => (
                <div className="flex-column full-height">
                    <div className="flex-grow-1">
                        <ValidatableLabel
                            className="mb-30"
                            label="Title"
                            required
                            error={errors.title}
                        >
                            <TextInput
                                autoFocus
                                placeholder="Enter a title"
                                className="full-width mb-5"
                                invalid={!!errors.title}
                                disabled={
                                    dashboard.PowerBiReport &&
                                    !canEditPowerBiReportDashboards
                                }
                                {...register('title', titleRules)}
                            />
                        </ValidatableLabel>
                        {dashboard.PowerBiReport && (
                            <Label className="mb-30" label="Sentry Dashboard URL">
                                {dashboard.PowerBiReport.PowerBiReportUrl}
                            </Label>
                        )}
                        <Label className="my-30" label="Description">
                            <Textarea
                                minRows={5}
                                placeholder="Enter a description"
                                className="my-5"
                                aria-invalid={errors.description !== undefined}
                                disabled={
                                    dashboard.PowerBiReport &&
                                    !canEditPowerBiReportDashboards
                                }
                                style={{
                                    minWidth: 150,
                                }}
                                {...register('description', descriptionRules)}
                            />
                            <FormError message={errors.description?.message} />
                        </Label>
                    </div>
                    <div className="justify-end">
                        <ModalHistoryInfoRow
                            type="created"
                            date={dashboard.Created}
                            user={dashboard.CreatedBy}
                        />
                        <ModalHistoryInfoRow
                            type="updated"
                            date={dashboard.Updated}
                            user={dashboard.UpdatedBy}
                        />
                    </div>
                </div>
            )}
        />
    );
}
